import { useMutation } from "@tanstack/react-query";
import { privateAxios, publicAxios } from "../axiosInstances";
import {
  SignupBody,
  SignupIndividualBody,
  BackofficeLoginBody,
  ResetPasswordRequestBody,
  DefinePasswordBody,
  ResendConfirmationMailBody,
  BackofficeAuthResponse,
  SignUpResponse,
  CreatePasswordBody,
} from "@ac-expo/api-types";
import { isAxiosError } from "axios";

type RemonteControlBody = {
  secret: string;
  connectId: string;
  accepted: boolean;
};

const backofficeLogin = async (body: BackofficeLoginBody) => {
  try {
    const { data } = await publicAxios.post<BackofficeAuthResponse>(
      "/auth/signin-backoffice",
      body,
      { withCredentials: true }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useBackofficeLoginMutation = () => {
  return useMutation({
    mutationFn: (data: BackofficeLoginBody) => backofficeLogin(data),
  });
};

export const signup = async (body: SignupBody) => {
  try {
    const { data } = await publicAxios.post<SignUpResponse>(
      "/auth/signup",
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useSignupMutation = () => {
  return useMutation({
    mutationFn: (data: SignupBody) => signup(data),
  });
};

export const signupIndividual = async (body: SignupIndividualBody) => {
  try {
    const { data } = await publicAxios.post<SignUpResponse>(
      "/auth/signup-individual",
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useSignupIndividualMutation = () => {
  return useMutation({
    mutationFn: (data: SignupIndividualBody) => signupIndividual(data),
  });
};

export const resendConfirmationMail = async (
  body: ResendConfirmationMailBody
) => {
  try {
    await publicAxios.post("/auth/resend-confirmation-mail", body);
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useResendConfirmationMailMutation = () => {
  return useMutation({
    mutationFn: (data: ResendConfirmationMailBody) =>
      resendConfirmationMail(data),
  });
};

export const resetPasswordRequest = async (body: ResetPasswordRequestBody) => {
  try {
    const { data } = await publicAxios.post("/auth/reset-password", body);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useResetPasswordRequestMutation = () => {
  return useMutation({
    mutationFn: (data: ResetPasswordRequestBody) => resetPasswordRequest(data),
  });
};

export const resetPassword = async (body: DefinePasswordBody) => {
  if (!body.token) return;

  try {
    const { data } = await publicAxios.post(
      `/auth/reset-password/${body.token}`,
      { password: body.password }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: (data: DefinePasswordBody) => resetPassword(data),
  });
};

export const createPassword = async (body: DefinePasswordBody) => {
  const { token, ...others } = body;

  if (!token) return;

  try {
    const { data } = await publicAxios.post(
      `/auth/create-password/${token}`,
      others
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useCreatePasswordMutation = () => {
  return useMutation({
    mutationFn: (data: CreatePasswordBody) => createPassword(data),
  });
};

const remoteControl = async (payload: RemonteControlBody) => {
  if (!payload.accepted) {
    throw new Error("Accepted is required");
  }
  const { data } = await privateAxios.post<{ success: boolean }>(
    "/auth/remote-control",
    payload
  );
  return data;
};

export const useRemoteControlMutation = () => {
  return useMutation({
    mutationFn: (data: RemonteControlBody) => remoteControl(data),
  });
};

export const logoutRemoteControl = async () => {
  const { data } = await privateAxios.post<void>(
    `/auth/logout-remote-control`,
    {}
  );
  return data;
};

export const useLogoutRemoteControl = () => {
  return useMutation({
    mutationFn: logoutRemoteControl,
  });
};
